/**
 * "Signed in elsewhere".
 *
 * The backend keeps one live agent session per account. When the same
 * account connects from another device it tells the old connection
 *
 *   { "type": "system", "status": "session_takeover",
 *     "reason": "replaced_by_new_connection", "timestamp": "2026-10-19T15:23:06Z" }
 *
 * and that connection is finished: the session now belongs to the other
 * device. This module recognises the frame, cuts this tab off from the
 * socket — the owner's teardown plus a flag that refuses any new socket —
 * and announces it once. Without the flag the client's automatic reconnect
 * would take the session straight back and the two devices would kick each
 * other for as long as both stayed open.
 *
 * The shopper stays signed in (2026-10-28; this device used to sign itself
 * out). The flag holds until the shopper asks for the session back —
 * `resumeSessionAfterTakeover()`, the chat panel's "Continue here" — or the
 * page reloads. Taking it back is a deliberate act for the same reason the
 * automatic reconnect is refused: it cuts the other device.
 *
 * What the app shows (the toast, the notice in the chat) lives in
 * AuthContext and the avatar widget. Deliberately framework-free (same shape
 * as toastManager) and without an import of utils/auth, which imports this
 * package.
 */
import { ServerMessageType } from "./orbieeChatSocket";

export const SESSION_TAKEOVER_STATUS = "session_takeover";

const state = {
  takenOver: false,
  /** @type {object | null} the frame that took the session */
  frame: null,
  /** @type {Set<(frame: object) => void>} */
  listeners: new Set(),
};

/** @returns {boolean} true for the backend's takeover frame, exactly as sent */
export function isSessionTakeoverFrame(frame) {
  return (
    !!frame &&
    typeof frame === "object" &&
    frame.type === ServerMessageType.SYSTEM &&
    frame.status === SESSION_TAKEOVER_STATUS
  );
}

/**
 * True while this tab has lost its session: from the frame until
 * `resumeSessionAfterTakeover()` or a page load.
 */
export function isSessionTakenOver() {
  return state.takenOver;
}

/** The frame that took the session, for diagnostics; null until then. */
export function getSessionTakeoverFrame() {
  return state.frame;
}

/**
 * `cb(frame)` runs each time the session is taken — once per takeover, so
 * again after a resume if another device connects once more. Returns the
 * unsubscribe. A subscriber added after the fact is not replayed; read
 * `isSessionTakenOver()` for the current state.
 */
export function subscribeSessionTakeover(cb) {
  if (typeof cb !== "function") return () => {};
  state.listeners.add(cb);
  return () => state.listeners.delete(cb);
}

/**
 * Watch a socket for the takeover frame. `teardown` is the owner's way of
 * closing and forgetting that socket (orbieeChatTurn.resetOrbieeChatSocket);
 * it runs before anyone is told, so no listener can race a reconnect.
 * Returns the unwatch function.
 */
export function watchSocketForTakeover(socket, teardown) {
  return socket.on("system", (frame) => {
    if (!isSessionTakeoverFrame(frame)) return;
    takeSession(frame, teardown);
  });
}

function takeSession(frame, teardown) {
  // One announcement per takeover, whatever the socket sends afterwards.
  if (state.takenOver) return;
  state.takenOver = true;
  state.frame = frame;
  try {
    teardown?.();
  } catch (err) {
    console.warn("[SessionTakeover] socket teardown threw", err);
  }
  console.warn("[SessionTakeover] session moved to another device", frame);
  [...state.listeners].forEach((cb) => {
    try {
      cb(frame);
    } catch (err) {
      console.error("[SessionTakeover] listener threw", err);
    }
  });
}

/**
 * The shopper wants the session back on this device ("Continue here").
 * Lifts the refusal so the next `connectOrbieeChatSocket` / turn opens a
 * fresh socket — which is what makes the backend hand the session back and
 * cut the other device. Does not connect by itself: the caller does, and
 * reports the outcome. Idempotent; returns whether a takeover was in force.
 */
export function resumeSessionAfterTakeover() {
  const wasTakenOver = state.takenOver;
  state.takenOver = false;
  state.frame = null;
  if (wasTakenOver) console.info("[SessionTakeover] resuming on this device");
  return wasTakenOver;
}

/** Forget a takeover silently. Tests call it; the app uses `resumeSessionAfterTakeover`. */
export function resetSessionTakeover() {
  state.takenOver = false;
  state.frame = null;
}
